var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useRef, useCallback } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { useNetworkString } from '../context/NetworkContext';
import { useAdminAddress } from '../context/AdminAddressContext';
import { fetchUserPositionCapId, fetchUserPositionCapIds } from '../utils/positionUtils';
export function usePositionCap(mode) {
    var _a;
    const currentAccount = useCurrentAccount();
    const { effectiveAddress, isAdminMode } = useAdminAddress();
    const network = useNetworkString();
    const hasInitializedFromCache = useRef(false);
    const isSingle = mode === 'single';
    // Both modes are admin-aware: prefer effectiveAddress (URL param) over wallet address.
    // In admin mode effectiveAddress is the URL-provided address; otherwise it is the
    // connected wallet address — so this works correctly for both modes.
    const address = effectiveAddress !== null && effectiveAddress !== void 0 ? effectiveAddress : currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address;
    const cachePrefix = `position-cap-id${isSingle ? '' : 's'}`;
    const [data, setData] = useState(() => {
        // On init, fall back to last-connected-address only when not in admin mode
        const initAddr = address || (!isAdminMode ? localStorage.getItem('last-connected-address') : null);
        const initNetwork = network || localStorage.getItem('last-network') || 'mainnet';
        if (!initAddr)
            return isSingle ? null : [];
        const cached = localStorage.getItem(`${cachePrefix}-${initNetwork}-${initAddr}`);
        if (!cached)
            return isSingle ? null : [];
        hasInitializedFromCache.current = true;
        if (isSingle)
            return cached;
        try {
            return JSON.parse(cached);
        }
        catch (_a) {
            return [];
        }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Mark loading during render when `address` changes so descendants (e.g. App's
    // useWalletTracker) do not run useEffect with positionsLoading === false while
    // positionCapIds still reflect the previous wallet — parent effects run after child effects.
    const renderSyncedAddressRef = useRef(undefined);
    if (renderSyncedAddressRef.current !== address) {
        renderSyncedAddressRef.current = address;
        if (address) {
            setLoading(true);
        }
        else {
            setLoading(false);
        }
    }
    const refetchPositionCaps = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!address) {
            if (!hasInitializedFromCache.current)
                setData(isSingle ? null : []);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        const cacheKey = `${cachePrefix}-${network}-${address}`;
        try {
            if (isSingle) {
                const id = yield fetchUserPositionCapId(network, address);
                setData(id);
                hasInitializedFromCache.current = false;
                if (!isAdminMode) {
                    localStorage.setItem('last-connected-address', address);
                    localStorage.setItem('last-network', network);
                }
                if (id) {
                    localStorage.setItem(cacheKey, id);
                }
                else {
                    localStorage.removeItem(cacheKey);
                }
            }
            else {
                const ids = yield fetchUserPositionCapIds(network, address);
                setData(ids || undefined);
                hasInitializedFromCache.current = false;
                if (!isAdminMode) {
                    localStorage.setItem('last-connected-address', address);
                    localStorage.setItem('last-network', network);
                }
                if (ids && ids.length > 0) {
                    localStorage.setItem(cacheKey, JSON.stringify(ids));
                }
                else {
                    localStorage.removeItem(cacheKey);
                }
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : `Failed to fetch position cap ID${isSingle ? '' : 's'}`);
            console.error(`❌ Error fetching position cap ID${isSingle ? '' : 's'}:`, err);
        }
        finally {
            setLoading(false);
        }
    }), [address, cachePrefix, isAdminMode, isSingle, network]);
    useEffect(() => {
        void refetchPositionCaps();
    }, [refetchPositionCaps]);
    useEffect(() => {
        const onTxSuccess = () => {
            void refetchPositionCaps();
        };
        document.addEventListener('transactionSuccess', onTxSuccess);
        return () => document.removeEventListener('transactionSuccess', onTxSuccess);
    }, [refetchPositionCaps]);
    if (isSingle) {
        return { positionCapId: data, loading, error };
    }
    return { positionCapIds: (_a = data) !== null && _a !== void 0 ? _a : [], loading, error };
}
